import './SaturationAndDecay.scss';
import cn from 'classnames';
import * as R from 'ramda';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { CartesianGrid, Line, LineChart as LC, Tooltip, XAxis, YAxis } from 'recharts';
import { DecayData, SatData } from '@blisspointmedia/bpm-types/dist/MMM';
import {
  capitalizeWords,
  currencyFormatter,
  MedianOrMean,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { ChartContainer } from '@Components/ChartContainer';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { LineChart } from '@Components/Charts/LineChart';
import { renderXAxisLabel, X_AXIS_LABEL_HEIGHT } from '@Components/Charts/XAxisLabel';
import {
  decayDataFormatter,
  saturationDataFormatter
} from '@Components/Charts/ModelResults/DataFormatters';
import { Design, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { WidgetContainer } from '@Components/WidgetContainer';
import { CONVERSION_FORMATTER_TYPE } from '@Pages/MMM/mmm-utils';
import IncrementalityIcon from '../../../assets/IncrementalityIcon.svg?react';
import { Brand100, getColorMap as colors, Neutral500 } from '../../../utils/colors';
import { downloadPNG, exportToExcel } from '../../../utils/download-utils';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';

const MEDIAN_DOT_SIZE = 7;
// No palette equivalent — this legend "inactive" gray is intentionally lighter than Neutral500.
const INACTIVE_COLOR = '#d3d3d3';

type FormattedSatData = ReturnType<typeof saturationDataFormatter>;
// Loosely typed to match the decayDataFormatter output whose nullable `grouping` isn't
// assignable to LineChart's stricter lineDataKeys prop (matches GeoMMM ModelInsights).
interface FormattedDecayData {
  newD: any[];
  lines: any[];
  groupedLines: any[];
}

const EMPTY_FORMATTED_SAT_DATA: FormattedSatData = {
  newD: [],
  centralMeasures: {},
  groupedLines: []
};

const conversionFormatter = (kpiType: string) =>
  kpiType === 'revenue' ? CONVERSION_FORMATTER_TYPE.revenue : CONVERSION_FORMATTER_TYPE.conversion;

interface CustomizedDotProps {
  saturationData: FormattedSatData;
  centralMeasure: MedianOrMean;
  // recharts injects these when it clones the dot element.
  cx?: number;
  cy?: number;
  stroke?: string;
  payload?: any;
}

const CustomizedDot = ({
  saturationData,
  centralMeasure,
  cx = 0,
  cy = 0,
  stroke,
  payload
}: CustomizedDotProps) => {
  const measure = saturationData?.centralMeasures?.[payload.name];
  if (
    !measure ||
    measure.dataPoint !== payload.adstock_spend ||
    measure[centralMeasure.toLowerCase()] === null
  ) {
    return null;
  }

  return (
    <svg>
      <polygon
        points={`${cx},${cy - MEDIAN_DOT_SIZE} ${cx + MEDIAN_DOT_SIZE},${cy} ${cx},${
          cy + MEDIAN_DOT_SIZE
        } ${cx - MEDIAN_DOT_SIZE},${cy}`}
        fill={stroke}
      />
    </svg>
  );
};

interface SaturationTooltipContentProps {
  centralMeasure: MedianOrMean;
  kpiType: string;
  // Deep dynamic chart data — centralMeasures values are keyed by the (any) recharts payload
  // name and may be null, so the currency formatter is fed values the strict type would reject.
  formattedSatData: any;
  // recharts injects these when it clones the content element.
  active?: boolean;
  payload?: any[];
}

const SaturationTooltipContent = ({
  centralMeasure,
  kpiType,
  formattedSatData,
  active,
  payload
}: SaturationTooltipContentProps) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const items = payload
    .map(item => ({
      label: item.name as string,
      value: item.value as number,
      color: item.stroke
    }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  return (
    <div className='sat-chart-tooltip'>
      <div className='item-list'>
        {items.map((item, i) => {
          const { label, value, color } = item;
          if (!value && value !== 0) {
            return null;
          }
          const cpa = payload[0].payload.adstock_spend / payload[0].payload.response;

          return (
            <div className='item-row' key={i}>
              <div className='item-wrapper data-label'>
                <div />
                <div className='data-label-content'>
                  <div className='line' style={{ backgroundColor: color }} />
                  <div className='name'>{`${label}: `}</div>
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>{centralMeasure} Spend:</div>
                <div className='value'>
                  {currencyFormatter.format(
                    formattedSatData.centralMeasures[payload[0].payload.name][
                      centralMeasure.toLowerCase()
                    ],
                    0
                  )}
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>Spend:</div>
                <div className='value'>
                  {currencyFormatter.format(payload[0].payload.adstock_spend, 0)}
                </div>
              </div>
              <div className='item-wrapper'>
                <div className='sat-tooltip-header-item'>{`${
                  kpiType === 'revenue' ? 'Revenue' : 'KPI Volume'
                }:`}</div>
                <div className='value'>
                  {conversionFormatter(kpiType).format(payload[0].payload.response, 0)}
                </div>
              </div>
              {kpiType === 'revenue' && (
                <div className='item-wrapper'>
                  <div className='sat-tooltip-header-item'>ROAS:</div>
                  <div className='value'>
                    {(payload[0].payload.response / payload[0].payload.adstock_spend).toFixed(2)}
                  </div>
                </div>
              )}
              {kpiType !== 'revenue' && (
                <div className='item-wrapper'>
                  <div className='sat-tooltip-header-item'>CPA:</div>
                  <div className='value'>{`${
                    cpa === Infinity ? 0 : currencyFormatter.format(cpa)
                  }`}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface SatAndDecayProps {
  disabledPlatform: boolean | undefined;
  kpiType: string;
  satData: SatData[];
  decayData: DecayData[];
  renderOmitDarkInputsToggle: boolean;
  omitDarkInputs: boolean;
  setOmitDarkInput: Dispatch<SetStateAction<boolean>>;
  centralMeasure: MedianOrMean;
}

export const SatAndDecay = ({
  disabledPlatform,
  kpiType,
  satData,
  decayData,
  renderOmitDarkInputsToggle,
  omitDarkInputs,
  setOmitDarkInput,
  centralMeasure
}: SatAndDecayProps) => {
  const locationCompany = useCompanyInfo().cid;
  const company = TEST_COMPANIES.includes(locationCompany) ? 'test' : locationCompany;
  const [decayDataFormatted, setDecayDataFormatted] = useState<FormattedDecayData | null>(null);
  const [activeLine, setActiveLine] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + X_AXIS_LABEL_HEIGHT + 5,
    yAxisWidthModifier: x => x + 8
  });

  useEffect(() => {
    if (decayData.length && disabledPlatform !== undefined) {
      setDecayDataFormatted(
        decayDataFormatter(decayData, 'spend_carryover', disabledPlatform, companyEqualsTest)
      );
    }
  }, [company, companyEqualsTest, disabledPlatform, decayData]);

  const excelDownloadSat = useCallback(() => {
    exportToExcel(satData, `${company}_saturation`);
  }, [satData, company]);

  const pngDownloadSat = useCallback(async () => {
    await downloadPNG(
      '.rmmm-sat-decay .left .chart-container',
      `${company}_saturation`,
      '',
      [],
      true,
      true
    );
  }, [company]);

  const excelDownloadDecay = useCallback(() => {
    exportToExcel(decayData, `${company}_decay`);
  }, [decayData, company]);

  const pngDownloadDecay = useCallback(async () => {
    await downloadPNG(
      '.rmmm-sat-decay .right .chart-container',
      `${company}_decay`,
      '',
      [],
      true,
      true
    );
  }, [company]);

  const formattedSatData = useMemo(() => {
    if (satData.length === 0 || disabledPlatform === undefined) {
      return EMPTY_FORMATTED_SAT_DATA;
    }
    return saturationDataFormatter(satData, disabledPlatform, centralMeasure, companyEqualsTest);
  }, [companyEqualsTest, disabledPlatform, satData, centralMeasure]);

  const colorMap = useMemo(() => colors(formattedSatData.newD), [formattedSatData]);

  const handleLegendClick = useCallback(
    (line, group?) => {
      if (activeLine === line.name) {
        setActiveLine(null);
        setActiveGroup(null);
      } else {
        setActiveLine(line.name);
        setActiveGroup(group.channel);
      }
    },
    [activeLine]
  );

  const handleLegendEnter = useCallback(
    line => {
      setHoveredLegend(hoveredLegend === line.name ? null : line.name);
    },
    [hoveredLegend]
  );

  const buildLegendByGroupedArea = useCallback(
    groupedAreas =>
      groupedAreas.map((group, i) => (
        <div className='legend-item' key={`satDecayLegenditem${i}`}>
          <div className='legend-value'>
            <div>
              <div className='legend-header-wrapper'>
                <div
                  className={cn('legend-header', {
                    'inactive-legend': !(activeGroup === group.channel || activeGroup === null)
                  })}
                >
                  {group.channel}
                </div>
              </div>
              {group.areas.map((area, j) => {
                const isActiveArea = activeLine === null ? true : activeLine === area.name;
                const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === area.name;
                const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
                const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
                return (
                  <InteractiveDiv
                    key={`satDecayLegendsubHeader${j}`}
                    className='sub-header-wrapper'
                    onClick={() => handleLegendClick(area, group)}
                    onMouseEnter={() => handleLegendEnter(area)}
                    onMouseLeave={() => setHoveredLegend(null)}
                  >
                    <div
                      className='line'
                      style={{ backgroundColor: `${activeLine ? activeColor : hoverColor}` }}
                    />
                    <div className='sub-header-text-wrap'>
                      <div className={cn('sub-header1', { 'inactive-legend': !isActiveArea })}>
                        <span>
                          {area.grouping.platform}
                          {area.incrementality &&
                            !(area.grouping.tactic || area.grouping.brand_nonbrand) && (
                              <>
                                {' '}
                                <IncrementalityIcon
                                  className={cn('incrementality-icon', {
                                    active: isActiveArea || activeLine === null,
                                    inactive: !(isActiveArea || activeLine === null)
                                  })}
                                />
                              </>
                            )}
                        </span>
                      </div>
                      {area.grouping.platform &&
                        (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                          <div style={{ whiteSpace: 'pre-wrap' }}> – </div>
                        )}
                      <div className={cn('sub-header2', { 'inactive-legend': !isActiveArea })}>
                        <span>
                          {`${area.grouping.tactic ? area.grouping.tactic : ''}${
                            area.grouping.tactic && area.grouping.brand_nonbrand ? ', ' : ''
                          }${area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ''}`}
                          {area.incrementality &&
                            (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                              <>
                                {' '}
                                <IncrementalityIcon
                                  className={cn('incrementality-icon', {
                                    active: isActiveArea || activeLine === null,
                                    inactive: !(isActiveArea || activeLine === null)
                                  })}
                                />
                              </>
                            )}
                        </span>
                      </div>
                    </div>
                  </InteractiveDiv>
                );
              })}
            </div>
          </div>
        </div>
      )),
    [activeLine, activeGroup, colorMap, handleLegendClick, handleLegendEnter, hoveredLegend]
  );

  return (
    <WidgetContainer
      footer={
        <div className='mmm-widget-bottom-wrapper'>
          <IncrementalityIcon className='incrementality-icon-bottom' />
          <div>
            Channels marked with a green beaker use informative priors based on incrementality
            studies. Incrementality testing is strongly recommended, and we encourage you to contact
            your team to review options.
          </div>
        </div>
      }
      collapsible
      header='Model Insights'
      subHeader='The model estimation process yields business insights related to channel saturation and the lagged nature of consumer response.'
      rightActions={
        renderOmitDarkInputsToggle ? (
          <ToggleSwitch
            design={Design.NEUTRAL}
            size={Size.LG}
            label='Omit Dark Inputs'
            checked={omitDarkInputs}
            onChange={setOmitDarkInput}
            infoTooltipText='Dark inputs are defined as paid media features that account for <1% of spend in the previous quarter. Omitting these inputs allows for focus on active investments only.'
          />
        ) : undefined
      }
    >
      <div className='rmmm-sat-decay'>
        <div className='left'>
          <ChartContainer
            title='Saturation Curves'
            rightActions={
              <>
                <InfoTooltip size={Size.MD}>
                  Most media tactics will exhibit some form of diminishing returns, due to
                  additional investment reaching less qualified audiences and/or additional user
                  frequency having diminished impact. The model training process estimates the shape
                  of these saturation curves on a channel by channel basis. Examining these curves
                  yields insight into the scalability of each channel and the optimality of
                  incremental investments (see above section).
                </InfoTooltip>
                <DownloadDropdown onClickOptions={[excelDownloadSat, pngDownloadSat]} />
              </>
            }
          >
            {formattedSatData.newD.length > 0 && (
              <>
                <div className='sat-wrapper'>
                  <div className='sat-chart-title'>
                    {kpiType === 'revenue' ? 'Revenue' : 'KPI Volume'}
                  </div>
                  <div className='sat-chart-box'>
                    <AutoSizer
                      renderProp={({ width, height }) => (
                        <LC width={width} height={height} ref={setChartRef}>
                          <CartesianGrid
                            stroke={CARTESIAN_GRID_STROKE}
                            strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                            vertical={false}
                          />
                          <XAxis
                            axisLine={false}
                            dataKey='adstock_spend'
                            height={xAxisHeight}
                            tick={TICK_STYLE}
                            tickFormatter={val => currencyFormatter.format(val, 0)}
                            type='number'
                            label={renderXAxisLabel('Spend')}
                          />
                          <YAxis
                            axisLine={false}
                            dataKey='response'
                            tick={TICK_STYLE}
                            tickFormatter={val => {
                              const decimals = val > 1000000 ? 1 : 0;
                              return conversionFormatter(kpiType).format(val, decimals);
                            }}
                            tickLine={false}
                            width={yAxisWidth}
                          />
                          {activeLine && (
                            <Tooltip
                              content={
                                <SaturationTooltipContent
                                  centralMeasure={centralMeasure}
                                  kpiType={kpiType}
                                  formattedSatData={formattedSatData}
                                />
                              }
                              isAnimationActive={false}
                            />
                          )}

                          {formattedSatData.newD.map(line => {
                            const isActiveLine =
                              activeLine === null ? true : activeLine === line.name;
                            const isHoveredLegend =
                              hoveredLegend === null ? true : hoveredLegend === line.name;
                            const activeColor = isActiveLine ? colorMap[line.name] : Neutral500;
                            const hoverColor = isHoveredLegend ? colorMap[line.name] : Neutral500;

                            if (!isActiveLine && activeLine !== null) {
                              return null;
                            }
                            return (
                              <Line
                                data={line.data}
                                dataKey='response'
                                dot={
                                  <CustomizedDot
                                    saturationData={formattedSatData}
                                    centralMeasure={centralMeasure}
                                  />
                                }
                                isAnimationActive={false}
                                key={line.name}
                                name={line.name}
                                stroke={activeLine ? activeColor : hoverColor}
                                strokeWidth={3}
                              />
                            );
                          })}
                        </LC>
                      )}
                    />
                  </div>
                </div>
                <div className='right-of-chart'>
                  <div className='legend'>
                    {buildLegendByGroupedArea(
                      R.sortBy(R.compose(R.toLower, R.prop('channel')))(
                        formattedSatData.groupedLines
                      )
                    )}
                  </div>
                  <div className='median-legend-label'>
                    <svg width='15' height='15' viewBox='0 0 15 15'>
                      <polygon points='7.5,0 15,7.5 7.5,15 0,7.5' fill={Brand100} />
                    </svg>
                    <div className='median-text'>
                      {centralMeasure} <br /> spend
                    </div>
                  </div>
                </div>
              </>
            )}
          </ChartContainer>
        </div>
        <div className='right'>
          <ChartContainer
            rightActions={
              <>
                <InfoTooltip size={Size.MD}>
                  Few consumers, upon being exposed to an advertisement, drop everything and respond
                  immediately; there is almost always some lag between exposure and response. The
                  model training process estimates the extent of this response lag on a channel by
                  channel basis using a technique known as Adstock. Typically (though not always),
                  we see upper-funnel tactics having longer response lags and lower-funnel tactics
                  converting more quickly. Examining these curves can help us understand channel
                  properties and set appropriate expectations for channel investments.
                </InfoTooltip>
                <DownloadDropdown onClickOptions={[excelDownloadDecay, pngDownloadDecay]} />
              </>
            }
            title='Spend Decay Curves'
          >
            <div className='decay-wrapper'>
              <div className='decay-chart-title'>Spend Carryover</div>
              {decayDataFormatted && (
                <LineChart
                  customTooltipHeader={val => `Week ${val} Spend Carryover`}
                  data={decayDataFormatted.newD}
                  lineDataKeys={decayDataFormatted.lines}
                  groupedAreas={R.sortBy(R.compose(R.toLower, R.prop('channel')))(
                    decayDataFormatted.groupedLines
                  )}
                  tooltipLabelFormatter={val => capitalizeWords(val, disabledPlatform, true)}
                  tooltipFormatter={val => (val ? `${numberFormatter.format(val, 0)}%` : val)}
                  usePercentageYAxis
                  xAxisDataKey='date'
                  xAxisTickFormatter={val => `Week ${val}`}
                />
              )}
            </div>
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};
